// [PURPOSE]: Register race trait effects (SRD + custom Content Workshop) into
//   effectRegistry on race/subrace change.
// [WHY]: useRaceEffects.js applies bulk race properties (speed, darkvision number,
//   damageResistance arrays, languages, hitPointBonus) directly to playerStats —
//   but it does not register effects into effectRegistry. That left racial trait
//   bonuses invisible to consumers like:
//     - SensesMovementPanel.getSensesFromEffects (queries 'sense' effects)
//     - effectCalculator.hasAdvantageOnCheck (queries 'advantage' effects)
//     - effectCalculator.getDamageResistances (also queries 'resistance' effects)
//     - effectCalculator.calculateSkillBonus (via getGrantedProficiencyLevel)
//     - effectCalculator.hasFeatureFlag (queries 'feature' grants)
// [INTEGRATION]: Mount once near the top of App.jsx alongside useRaceEffects /
//   useFeatEffects / useClassFeatureEffects. Mirrors useClassFeatureEffects
//   structure exactly.
// [Phase 4b bucket 4 — APR 2026]: Race-feature wiring layer that closes the
//   SRD trait gaps identified in the race audit.
// [Phase 4b CW-parity (B) — APR 2026]: Bridge for Content-Workshop-authored
//   custom races so player-built race effects round-trip identically to SRD ones.

import { useEffect } from 'react';
import { effectRegistry } from '../services/effectRegistry';
import { effectCalculator } from '../services/effectCalculator';
import { getActiveRaceFeatures } from '../data/srdRaceFeatures';
import { sourcesManager } from '../data/sourcesManager';
import { synthesizeRaceProficiencyEffects } from '../utils/raceProficiencyEffects';
import { synthesizeRacialInnateSpellEffects } from '../utils/racialInnateSpellEffects';

/**
 * [Phase 4b CW-parity (B)]: Walk a Content-Workshop-authored custom race's
 *   `featureEffects` map (and the active subrace's, if any), returning the
 *   merged tuples in the same shape as getActiveRaceFeatures.
 *
 * Custom race shape:
 *   {
 *     name, ...
 *     featureEffects: { [traitName]: { effects, choiceKey?, choices?, notes? } },
 *     subraces: {
 *       [subraceName]: {
 *         name, ...,
 *         featureEffects: { [traitName]: { effects?, choiceKey?, choices?, notes? } }
 *       }
 *     }
 *   }
 *
 * Subrace traits override base traits with the same name (matches SRD merge
 * semantics in getActiveRaceFeatures).
 *
 * Race traits don't carry a minLevel — they all apply at character creation,
 * so this returns ALL entries that have authored effects (or choice blocks).
 */
export function getActiveCustomRaceFeatures(customRace, subraceName = null) {
  if (!customRace) return [];
  const merged = { ...(customRace.featureEffects || {}) };

  if (subraceName && customRace.subraces && customRace.subraces[subraceName]) {
    const subFeatureEffects = customRace.subraces[subraceName].featureEffects || {};
    for (const [traitName, traitData] of Object.entries(subFeatureEffects)) {
      merged[traitName] = traitData;
    }
  }

  const entries = Object.entries(merged).filter(([, trait]) => {
    if (!trait) return false;
    const hasEffects = Array.isArray(trait.effects) && trait.effects.length > 0;
    const hasChoices = trait.choices && trait.choiceKey;
    return hasEffects || hasChoices;
  });

  // [EDITOR-RACE-prof-skill-bridge-FIX 1a, 2026-06-13]: the top-level
  //   Proficiencies grid (race.proficiencies.skills) stores labels that reached no
  //   skill reader. Synthesize `proficiency` grant effects at register-time so they
  //   flow through the same registerTrait → effectRegistry path as SRD races (and
  //   get teardown free via removeByPrefix('race:')). Storage stays the label array
  //   → zero migration. SKILLS only for now (verified reader); weapons/armor/tools
  //   queued as 1b. Trace: .claude/findings/2026-06-13-datamodel-race-prof-skillproficiency-bridge-rpb1.md.
  const profEffects = synthesizeRaceProficiencyEffects(customRace.proficiencies);
  if (subraceName && customRace.subraces?.[subraceName]?.proficiencies) {
    profEffects.push(...synthesizeRaceProficiencyEffects(customRace.subraces[subraceName].proficiencies));
  }
  if (profEffects.length) {
    entries.push(['Proficiencies', { effects: profEffects }]);
  }

  // [INNATE-SPELL-1, 2026-06-14]: the Granted Spells section (incl. G7
  //   castingAbility/unlockLevel/frequency) wrote race.grantedSpells that reached
  //   no consumer. Synthesize `racialInnateSpell` grant effects at register-time
  //   (same pattern as the proficiency synth above) so they surface in the
  //   spellbook via effectCalculator.getRacialInnateSpells. Storage unchanged →
  //   zero migration. Additive base + subrace (mirrors the proficiency merge).
  //   Trace: .claude/findings/2026-06-14-datamodel-racial-innate-spell-surfacing-ris1.md.
  const innateSpellEffects = synthesizeRacialInnateSpellEffects(customRace.grantedSpells);
  if (subraceName && customRace.subraces?.[subraceName]?.grantedSpells) {
    innateSpellEffects.push(...synthesizeRacialInnateSpellEffects(customRace.subraces[subraceName].grantedSpells));
  }
  if (innateSpellEffects.length) {
    entries.push(['Innate Spellcasting', { effects: innateSpellEffects }]);
  }

  return entries;
}

/**
 * Looks up the matching custom race entry by display name. Returns null if
 * none found or if the lookup fails. Match is by display name only — race
 * names are intended to be globally unique. SRD + custom both register when
 * names collide (intentional — lets players extend SRD races).
 */
function findCustomRace(raceName) {
  let all;
  try {
    all = sourcesManager.getAllRaces();
  } catch (err) {
    console.warn('[useRaceFeatureEffects] getAllRaces failed:', err);
    return null;
  }
  if (!all || typeof all !== 'object') return null;
  for (const entry of Object.values(all)) {
    if (entry && entry.name === raceName) return entry;
  }
  return null;
}

/**
 * Walks a dotted path inside playerStats. Used for race trait choice blocks
 * (e.g., Aasimar L3 transformation: choiceKey 'aasimarTransformation' on
 * playerStats.aasimarTransformation).
 */
function resolveRaceChoice(playerStats, choiceKey) {
  if (!playerStats || !choiceKey) return null;
  return choiceKey.split('.').reduce((acc, k) => (acc != null ? acc[k] : undefined), playerStats);
}

/**
 * Registers a single trait's effects (always-on + choice-dependent) under the
 * canonical prefix `race:<Race>[:<Subrace>]:<TraitName>`. Shared between SRD
 * and custom paths so attribution and cleanup are identical.
 */
function registerTrait({ race, subrace, traitName, traitData, playerStats }) {
  const baseId = subrace
    ? `race:${race}:${subrace}:${traitName}`
    : `race:${race}:${traitName}`;

  if (Array.isArray(traitData.effects) && traitData.effects.length) {
    try {
      effectRegistry.register(baseId, traitData.effects, playerStats);
    } catch (err) {
      console.warn(`[useRaceFeatureEffects] failed to register ${baseId}`, err);
    }
  }

  if (traitData.choices && traitData.choiceKey) {
    const selected = resolveRaceChoice(playerStats, traitData.choiceKey);
    const optionData = selected ? traitData.choices[selected] : null;
    if (optionData && Array.isArray(optionData.effects) && optionData.effects.length) {
      try {
        effectRegistry.register(`${baseId}:${selected}`, optionData.effects, playerStats);
      } catch (err) {
        console.warn(`[useRaceFeatureEffects] choice failed: ${baseId}:${selected}`, err);
      }
    }
  }
}

/**
 * [Phase 4b CW-parity (B)]: Top-level orchestrator — exported so the bridge
 *   can be exercised in unit tests without a React renderer. Walks
 *   playerStats.basics.race + subrace, registers SRD race traits, then merges
 *   in Content-Workshop-authored custom races for the same display name.
 *
 * Pure side-effect: writes to effectRegistry, then clears effectCalculator cache.
 */
export function registerRaceFeaturesForCharacter(playerStats) {
  effectRegistry.removeByPrefix('race:');

  const race = playerStats?.basics?.race || playerStats?.race || null;
  const subrace = playerStats?.basics?.subrace || playerStats?.subrace || null;

  if (!race) {
    effectCalculator.clearCache();
    return;
  }

  // 1. SRD race features
  const srdFeatures = getActiveRaceFeatures(race, subrace);
  for (const [traitName, traitData] of srdFeatures) {
    registerTrait({ race, subrace, traitName, traitData, playerStats });
  }

  // 2. Custom race features (CW-authored, stored in sourcesManager). Match by
  //    race name; SRD and custom both register when names collide.
  const customRace = findCustomRace(race);
  if (customRace) {
    const customFeatures = getActiveCustomRaceFeatures(customRace, subrace);
    for (const [traitName, traitData] of customFeatures) {
      registerTrait({ race, subrace, traitName, traitData, playerStats });
    }
  }

  effectCalculator.clearCache();
}

export function useRaceFeatureEffects(playerStats) {
  const race = playerStats?.basics?.race || playerStats?.race || null;
  const subrace = playerStats?.basics?.subrace || playerStats?.subrace || null;

  useEffect(() => {
    registerRaceFeaturesForCharacter(playerStats);

    return () => {
      effectRegistry.removeByPrefix('race:');
      effectCalculator.clearCache();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [race, subrace, playerStats]);
}

export default useRaceFeatureEffects;
